import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useT } from './useT';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from './index';
import { markLanguageSourceManual } from './useLanguageSync';

interface LanguageSelectorProps {
  className?: string;
}

/**
 * Language selector dropdown component with flag images.
 * Allows users to switch between supported languages.
 * 
 * @example
 * <LanguageSelector className="my-4" />
 */
export function LanguageSelector({ className = '' }: LanguageSelectorProps) {
  const { language: rawLanguage, changeLanguage } = useT();
  // Strip any region before matching: i18next may report 'de-DE' depending on
  // whether the value came from the detector or from a resolved lookup, and
  // 'de-DE' is not a key of SUPPORTED_LANGUAGES — which used to make the
  // selector display "English" while the UI rendered German.
  const primary = (rawLanguage || '').split('-')[0].toLowerCase();
  const language: SupportedLanguage = (primary in SUPPORTED_LANGUAGES)
    ? (primary as SupportedLanguage)
    : 'en';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: SupportedLanguage) => {
    // Record that the user has explicitly chosen a language so the
    // next sync reports `language_source: "manual"` to Customer.io.
    markLanguageSourceManual();
    changeLanguage(code);
    setIsOpen(false);
  };

  const getFlagUrl = (code: SupportedLanguage) => {
    const countryCode = SUPPORTED_LANGUAGES[code]?.flag;
    return `https://flagcdn.com/w40/${countryCode}.png`;
  };

  return (
    <div ref={dropdownRef} className={`relative inline-block ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-transparent border border-divider rounded-lg text-sm cursor-pointer hover:border-primary-500 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <img
          src={getFlagUrl(language)}
          alt={SUPPORTED_LANGUAGES[language]?.name}
          className="w-5 h-auto rounded-sm"
        />
        <span>{SUPPORTED_LANGUAGES[language]?.nativeName}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full min-w-[140px] bg-dark-surface border rounded-lg shadow-lg overflow-hidden"
        >
          {(Object.keys(SUPPORTED_LANGUAGES) as SupportedLanguage[]).map((code) => (
            <li key={code}>
              <button
                role="option"
                aria-selected={language === code}
                onClick={() => handleSelect(code)}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-primary-500/10 transition-colors ${
                  language === code ? 'bg-primary-500/10 font-medium' : ''
                }`}
              >
                <img
                  src={getFlagUrl(code)}
                  alt={SUPPORTED_LANGUAGES[code].nativeName}
                  className="w-5 h-auto rounded-sm"
                />
                <span>{SUPPORTED_LANGUAGES[code].nativeName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default LanguageSelector;

